import { useEffect, useRef } from 'react';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useEventsSubscriptionStateStore from '../../stores/useEventsSubscriptionStateStore';
import { dlog } from '../../dev';
import useInterval from '../useInterval';
import EVENTS_SUBSCRIPTION_UTILS from '../../utils/EventsSubscription/eventsSubscriptionUtils';
import useCurrentSettings from '../useCurrentSettings';

const GRACE_PERIOD_MS = 10_000;
const FALLUP_ATTEMPT_INTERVAL_MS = 1000 * 60;

/**
 * Hook responsible for syncing the subscription state store based on various conditions
 * like feature flags and settings
 */
const useSyncEventsSubscriptionState = () => {
	const dataApiTradesApiFlag = useFeatureFlagStore(
		(state) => state.flags.ENABLE_DATA_API_CANDLES_AND_TRADES_API
	);

	const updateGlobalEventSubscriberType = useEventsSubscriptionStateStore(
		(s) => s.updateGlobalEventSubscriberType
	);
	const updateSelfEventSubscriberType = useEventsSubscriptionStateStore(
		(s) => s.updateSelfEventSubscriberType
	);

	const alreadySyncedSelfEventsSubscriptionType = useRef(false);
	const alreadySyncedGlobalEventsSubscriptionType = useRef(false);

	// Handle initially syncing the Self Events Subscription state with asyncronous settings
	useEffect(() => {
		// Currently no asyncronous settings should affect the self subscription type
		// TODO :: Leaving this in here for posterity in case we ever add asyncronous settings which affect the self subscription type
		return;
	}, []);

	// Handle initially syncing the Global Events Subscription state with asyncronous settings
	useEffect(() => {
		if (alreadySyncedGlobalEventsSubscriptionType.current) return;

		// If we haven't synced the dataApiTradesApiFlag yet, we can skip until it has been synced. It is the only flag which should affect the global event subscriber type at the moment
		if (!dataApiTradesApiFlag.hasBeenSynced) return;

		const preferredGlobalEventSubscriptionType =
			EVENTS_SUBSCRIPTION_UTILS.getPreferredGlobalEventSubscriptionType({
				ignoreUserSettings: false,
			});
		dlog(
			`optimised_event_subscriptions`,
			`syncing_global_events_subscription_to:${preferredGlobalEventSubscriptionType}`
		);
		updateGlobalEventSubscriberType(preferredGlobalEventSubscriptionType);
		alreadySyncedGlobalEventsSubscriptionType.current = true;
	}, [dataApiTradesApiFlag, updateGlobalEventSubscriberType]);

	// Set the synced flags to true after a grace-period. We allow x amount of some for asyncronous stuff to happen otherwise just go with default handling.
	useEffect(() => {
		setTimeout(() => {
			alreadySyncedSelfEventsSubscriptionType.current = true;
			alreadySyncedGlobalEventsSubscriptionType.current = true;
		}, GRACE_PERIOD_MS);
	}, []);

	// Attempt to fall up every x amount of time
	useInterval(() => {
		dlog(`optimised_event_subscriptions`, `fall_up_attempt_interval_running`);
		useEventsSubscriptionStateStore.getState().handleGlobalEventsFallbackUp();
	}, FALLUP_ATTEMPT_INTERVAL_MS);

	// Handle user settings changes
	const [userSettings] = useCurrentSettings();

	// Handle changes to global events subscription type
	useEffect(() => {
		// Don't account for any user settings until the sync has run
		if (!alreadySyncedGlobalEventsSubscriptionType.current) return;

		const newGlobalSetting =
			EVENTS_SUBSCRIPTION_UTILS.getPreferredGlobalEventSubscriptionType({
				ignoreUserSettings: false,
			});

		// If we're here then the setting has changed after the initial sync
		if (userSettings.globalEventsSubscriptionType) {
			// Handle setting being set
			dlog(
				`optimised_event_subscriptions`,
				`user_settings_changed:global_subscription_set_to_value::changing_to:${newGlobalSetting}`
			);
		} else {
			// Handle setting being reset
			dlog(
				`optimised_event_subscriptions`,
				`user_settings_changed::global_subscription_reset_to_default::changing_to:${newGlobalSetting}`
			);
		}

		updateGlobalEventSubscriberType(newGlobalSetting);
	}, [userSettings.globalEventsSubscriptionType]);

	// Handle changes to self events subscription type
	useEffect(() => {
		if (!alreadySyncedSelfEventsSubscriptionType.current) return;

		const newSelfSetting =
			EVENTS_SUBSCRIPTION_UTILS.getPreferredSelfSubscriptionType({
				ignoreUserSettings: false,
			});

		if (userSettings.selfEventsSubscriptionType) {
			// Handle setting being set
			dlog(
				`optimised_event_subscriptions`,
				`user_settings_changed:self_subscription_set_to_value::changing_to:${newSelfSetting}`
			);
		} else {
			// Handle setting being reset
			dlog(
				`optimised_event_subscriptions`,
				`user_settings_changed::self_subscription_reset_to_default::changing_to:${newSelfSetting}`
			);
		}

		updateSelfEventSubscriberType(newSelfSetting);
	}, [userSettings.selfEventsSubscriptionType]);
};

export default useSyncEventsSubscriptionState;
